import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import {
  Key, Bot, TrendingUp, Activity, ArrowRight,
  Mic, MessageSquare, Users, Volume2, Gauge,
  ShieldCheck, Database, Zap, AlertTriangle
} from 'lucide-react';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { apiClient } from '@/lib/api';
import { KpiCard } from '@/pages/logistics/riepilogo/shared';
import type { LucideIcon } from 'lucide-react';

const PROVIDER_CSS_VARS: Record<string, string> = {
  openai: 'var(--chart-openai)',
  elevenlabs: 'var(--chart-elevenlabs)',
  claude: 'var(--chart-claude)',
  google: 'var(--chart-google)',
};

const PROVIDER_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  elevenlabs: 'ElevenLabs',
  claude: 'Claude',
  google: 'Gemini',
};

function getProviderChartColor(provider: string): string {
  return PROVIDER_CSS_VARS[provider] || 'var(--chart-fallback)';
}

const staggerContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const fadeSlideUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' as const } },
};

function GlassCard({ children, className = '', accentVar = '' }: {
  children: React.ReactNode; className?: string; accentVar?: string;
}) {
  return (
    <div className={`
      relative overflow-hidden rounded-2xl
      border border-border
      bg-card
      backdrop-blur-xl
      shadow-sm
      transition-all duration-300 hover:shadow-md
      ${className}
    `} style={{ '--glass-shadow': 'var(--glass-shadow)' } as React.CSSProperties}>
      {accentVar && (
        <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: accentVar }} />
      )}
      <div className="absolute inset-0 bg-gradient-to-br from-card/80 to-transparent pointer-events-none" />
      <div className="relative">{children}</div>
    </div>
  );
}

interface ProviderStatusEntry {
  active: boolean;
  configured?: boolean;
  connection_valid?: boolean;
}

interface ProviderStatusMap {
  openai?: { active: boolean };
  claude?: ProviderStatusEntry;
  google?: ProviderStatusEntry;
  elevenlabs?: ProviderStatusEntry;
}

interface ActiveProviderInfo {
  is_valid: boolean;
}

interface AgentBase {
  id: string;
  name: string;
  is_active: boolean;
}

interface ElevenLabsAgentBase extends AgentBase {
  elevenlabs_agent_id?: string;
}

interface TimeSeriesEntry {
  date: string;
  openai_cost: number;
  elevenlabs_cost: number;
  claude_cost: number;
  google_cost: number;
}

interface ProviderTotalEntry {
  estimated_cost?: number;
}

interface AgentUsageEntry {
  agent_id: string;
  total_tokens?: number;
  request_count?: number;
}

interface EnhancedAnalyticsData {
  time_series: TimeSeriesEntry[];
  provider_totals: Record<string, ProviderTotalEntry>;
  agents: AgentUsageEntry[];
  model_breakdown?: Array<{ pricing_unknown?: boolean; model?: string }>;
  grand_total?: {
    total_tokens: number;
    total_requests: number;
    total_cost: number;
  };
}

interface OpenaiRealUsageData {
  source: string;
  valid: boolean;
  total_real_cost: number;
  daily_costs: Array<{ date: string; cost: number }>;
}

interface ElevenLabsAnalyticsData {
  stats: {
    total_conversations: number;
    total_duration: number;
    avg_duration: number;
    completed: number;
    failed: number;
  };
  recent: Array<{ id: string; agent_name: string; status: string }>;
  daily: Array<{ date: string; count: number; total_duration: number }>;
}

interface RagStatsPerformance {
  total_queries: number;
  avg_total_ms: number;
  p95_total_ms: number;
  cache_hit_rate: string;
  cache_hits: number;
  cache_misses: number;
}

interface RagStatsResponse {
  performance: RagStatsPerformance;
}

interface DashboardOverviewProps {
  activeProvider: ActiveProviderInfo | undefined;
  providerStatus: ProviderStatusMap | null;
  agents: AgentBase[];
  chatbotAgents: AgentBase[];
  personalAssistants: AgentBase[];
  elevenLabsAgents: ElevenLabsAgentBase[];
  geminiVoiceAgents?: AgentBase[];
  aiEnabled: boolean;
  setAiEnabled: (v: boolean) => void;
  enhancedData?: EnhancedAnalyticsData | null;
  openaiRealUsage?: OpenaiRealUsageData | null;
  elevenLabsAnalytics: ElevenLabsAnalyticsData | null;
  setActiveTab: (tab: string) => void;
}

const tooltipStyle: React.CSSProperties = {
  background: 'var(--tooltip-bg)',
  border: '1px solid var(--tooltip-border)',
  color: 'var(--tooltip-text)',
  borderRadius: 10,
  fontSize: 12,
};

function StatusDot({ status }: { status: 'active' | 'configured' | 'inactive' }) {
  const bg = status === 'active'
    ? 'var(--status-success)'
    : status === 'configured'
      ? 'var(--status-warning)'
      : 'var(--status-inactive)';
  return <div className="w-2 h-2 rounded-full" style={{ backgroundColor: bg }} />;
}

export default function DashboardOverview({
  activeProvider, providerStatus, agents, chatbotAgents,
  personalAssistants, elevenLabsAgents, geminiVoiceAgents = [], aiEnabled, setAiEnabled,
  enhancedData: enhancedDataProp, openaiRealUsage: openaiRealUsageProp, elevenLabsAnalytics, setActiveTab,
}: DashboardOverviewProps) {

  const pendingPollCountRef = useRef(0);
  const MAX_PENDING_POLLS = 3;

  const { data: _enhancedQuery } = useQuery({
    queryKey: ['ai-dashboard-enhanced'],
    queryFn: async () => {
      type EnhancedPayload = Partial<EnhancedAnalyticsData> & {
        external_data_pending?: boolean;
        openai_real_usage?: OpenaiRealUsageData | null;
      };
      const res = await apiClient.get<{ data?: EnhancedPayload } & EnhancedPayload>('/ai-tools/analytics/enhanced?period=30d');
      const eData = res?.data?.data ?? res?.data ?? null;
      const pending = eData?.external_data_pending ?? false;
      if (pending) {
        pendingPollCountRef.current++;
      } else {
        pendingPollCountRef.current = 0;
      }
      return {
        enhanced: eData,
        openaiRealUsage: eData?.openai_real_usage?.valid ? eData.openai_real_usage : null,
        externalDataPending: pending,
      };
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: !enhancedDataProp,
    refetchInterval: (query) => {
      const pending = query?.state?.data?.externalDataPending;
      if (pending && pendingPollCountRef.current < MAX_PENDING_POLLS) return 4000;
      return false;
    },
  });

  const { data: ragStats = null } = useQuery<RagStatsResponse | null>({
    queryKey: ['ai-dashboard-rag-stats'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: RagStatsResponse }>('/rag-analytics/stats?days=7');
      const payload = res as unknown as { data?: RagStatsResponse };
      return payload.data ?? null;
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { data: localElAnalytics = null } = useQuery<ElevenLabsAnalyticsData | null>({
    queryKey: ['ai-dashboard-el-analytics'],
    queryFn: async () => {
      const res = await apiClient.get<ElevenLabsAnalyticsData>('/elevenlabs/analytics');
      const payload = res as { data?: ElevenLabsAnalyticsData };
      return payload.data ?? null;
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: !elevenLabsAnalytics,
  });

  const enhancedData = enhancedDataProp ?? _enhancedQuery?.enhanced ?? null;
  const openaiRealUsage = openaiRealUsageProp ?? _enhancedQuery?.openaiRealUsage ?? null;

  const providerCount = [
    activeProvider?.is_valid,
    providerStatus?.claude?.active || (providerStatus?.claude?.configured && providerStatus?.claude?.connection_valid),
    providerStatus?.google?.active || (providerStatus?.google?.configured && providerStatus?.google?.connection_valid),
    providerStatus?.elevenlabs?.active || (providerStatus?.elevenlabs?.configured && providerStatus?.elevenlabs?.connection_valid),
  ].filter(Boolean).length;

  const totalAgents = agents.length + chatbotAgents.length + personalAssistants.length + elevenLabsAgents.length + geminiVoiceAgents.length;
  const activeAgentsCount = agents.filter(a => a.is_active).length + chatbotAgents.filter(a => a.is_active).length
    + personalAssistants.filter(a => a.is_active).length + elevenLabsAgents.filter(a => a.is_active).length + geminiVoiceAgents.filter(a => a.is_active).length;

  const costDisplay = openaiRealUsage?.valid ? `${openaiRealUsage.total_real_cost.toFixed(2)} €` : '—';
  const costSub = openaiRealUsage?.valid ? `${openaiRealUsage.daily_costs.length} giorni tracciati` : 'Configura API';

  const partialPricing = useMemo(() => {
    const mb = (enhancedData)?.model_breakdown as Array<{ pricing_unknown?: boolean; model?: string }> | undefined;
    if (!Array.isArray(mb) || mb.length === 0) return { has: false, count: 0 };
    const unknown = mb.filter(m => m?.pricing_unknown);
    return { has: unknown.length > 0, count: unknown.length };
  }, [enhancedData]);

  const trendData = useMemo(() => {
    if (!enhancedData?.time_series?.length) return [];
    const byDate: Record<string, number> = {};
    for (const entry of enhancedData.time_series) {
      const d = entry.date?.slice(0, 10) || '';
      if (!d) continue;
      const cost = (Number(entry.openai_cost) || 0)
        + (Number(entry.elevenlabs_cost) || 0) + (Number(entry.claude_cost) || 0)
        + (Number(entry.google_cost) || 0);
      byDate[d] = (byDate[d] || 0) + cost;
    }
    return Object.entries(byDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-7)
      .map(([date, cost]) => ({
        date: new Date(date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }),
        costo: Number(cost.toFixed(4)),
      }));
  }, [enhancedData]);

  const donutData = useMemo(() => {
    if (!enhancedData?.provider_totals) return [];
    return Object.entries(enhancedData.provider_totals)
      .map(([provider, data]) => ({
        name: PROVIDER_LABELS[provider] || provider,
        value: Number(data.estimated_cost) || 0,
        color: getProviderChartColor(provider),
      }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [enhancedData]);

  const topAgents = useMemo(() => {
    const allAgents: Array<{ id: string; name: string; type: string; typeColor: string; is_active: boolean; icon: LucideIcon }> = [
      ...agents.map(a => ({ id: a.id, name: a.name, type: 'vocale', typeColor: 'bg-primary/15 text-primary', is_active: a.is_active, icon: Mic })),
      ...elevenLabsAgents.map(a => ({ id: a.id || a.elevenlabs_agent_id || '', name: a.name, type: 'EL', typeColor: 'bg-accent text-accent-foreground', is_active: a.is_active, icon: Volume2 })),
      ...geminiVoiceAgents.map(a => ({ id: a.id, name: a.name, type: 'GV', typeColor: 'bg-blue-100 text-blue-700', is_active: a.is_active, icon: Mic })),
      ...chatbotAgents.map(a => ({ id: a.id, name: a.name, type: 'chatbot', typeColor: 'bg-secondary text-secondary-foreground', is_active: a.is_active, icon: MessageSquare })),
      ...personalAssistants.map(a => ({ id: a.id, name: a.name, type: 'assist.', typeColor: 'bg-muted text-muted-foreground', is_active: a.is_active, icon: Users })),
    ];

    const usageMap: Record<string, number> = {};
    if (enhancedData?.agents) {
      for (const au of enhancedData.agents) {
        usageMap[au.agent_id] = (usageMap[au.agent_id] || 0) + (Number(au.total_tokens) || Number(au.request_count) || 0);
      }
    }

    return allAgents
      .map(a => ({ ...a, usage: usageMap[a.id] || 0 }))
      .sort((a, b) => b.usage - a.usage || (b.is_active ? 1 : 0) - (a.is_active ? 1 : 0))
      .slice(0, 5);
  }, [agents, chatbotAgents, personalAssistants, elevenLabsAgents, geminiVoiceAgents, enhancedData]);

  const maxAgentUsage = Math.max(...topAgents.map(a => a.usage), 1);

  const ragLatency = ragStats?.performance?.avg_total_ms ? `${ragStats.performance.avg_total_ms}ms` : '—';
  const ragCacheHits = ragStats?.performance?.cache_hits ?? 0;
  const ragCacheMisses = ragStats?.performance?.cache_misses ?? 0;
  const ragCacheRate = (ragCacheHits + ragCacheMisses) > 0
    ? `${Math.round((ragCacheHits / (ragCacheHits + ragCacheMisses)) * 100)}%`
    : '—';

  const effectiveElAnalytics = elevenLabsAnalytics ?? localElAnalytics;
  const elTotal = Number(effectiveElAnalytics?.stats?.total_conversations) || 0;
  const elCompleted = Number(effectiveElAnalytics?.stats?.completed) || 0;
  const elSuccessRate = elTotal > 0 ? `${Math.round((elCompleted / elTotal) * 100)}%` : '—';

  const providerUptime = useMemo(() => {
    if (!providerStatus) return '—';
    const providers = ['claude', 'google', 'elevenlabs'] as const;
    const configured = providers.filter(p => providerStatus[p]?.configured);
    if (configured.length === 0 && !activeProvider?.is_valid) return '—';
    const totalConfigured = configured.length + (activeProvider?.is_valid ? 1 : 0);
    const totalActive = configured.filter(p => providerStatus[p]?.active).length + (activeProvider?.is_valid ? 1 : 0);
    return `${Math.round((totalActive / totalConfigured) * 100)}%`;
  }, [providerStatus, activeProvider]);

  function getProviderDotStatus(p: ProviderStatusEntry | undefined): 'active' | 'configured' | 'inactive' {
    if (p?.active) return 'active';
    if (p?.configured) return 'configured';
    return 'inactive';
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          icon={Key}
          tone="indigo"
          value={`${providerCount}/4`}
          label="Provider AI Configurati"
          index={0}
          badge={
            <Badge
              style={{
                backgroundColor: activeProvider?.is_valid ? 'var(--status-success-bg)' : undefined,
                color: activeProvider?.is_valid ? 'var(--status-success-text)' : undefined,
              }}
              className={activeProvider?.is_valid ? '' : 'bg-muted text-muted-foreground'}
            >
              {activeProvider?.is_valid ? 'Connesso' : 'Non connesso'}
            </Badge>
          }
          footer={
            <div className="flex items-center gap-1.5">
              <StatusDot status={activeProvider?.is_valid ? 'active' : 'inactive'} />
              <StatusDot status={getProviderDotStatus(providerStatus?.claude)} />
              <StatusDot status={getProviderDotStatus(providerStatus?.google)} />
              <StatusDot status={getProviderDotStatus(providerStatus?.elevenlabs)} />
            </div>
          }
        />

        <KpiCard
          icon={Bot}
          tone="violet"
          value={totalAgents}
          label="Agenti AI Totali"
          index={1}
          badge={
            <Badge style={{ backgroundColor: 'var(--status-success-bg)', color: 'var(--status-success-text)' }}>
              {activeAgentsCount} attivi
            </Badge>
          }
          footer={
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground flex-wrap">
              <span>{agents.length} vocali</span><span>{'·'}</span>
              <span>{elevenLabsAgents.length} EL</span><span>{'·'}</span>
              <span>{geminiVoiceAgents.length} GV</span><span>{'·'}</span>
              <span>{chatbotAgents.length} chatbot</span><span>{'·'}</span>
              <span>{personalAssistants.length} assist.</span>
            </div>
          }
        />

        <KpiCard
          icon={TrendingUp}
          tone="amber"
          value={costDisplay}
          label="Costi Mese Corrente"
          sub={costSub}
          index={2}
          badge={partialPricing.has ? (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 dark:bg-amber-950/30 border border-amber-200/60 px-1.5 py-0.5 rounded-full"
              title={`${partialPricing.count} modello/i senza prezzo nel catalogo (llm_model_pricing). Il totale potrebbe essere sottostimato.`}
            >
              <AlertTriangle className="h-3 w-3" />
              Prezzi parziali
            </span>
          ) : undefined}
        />

        <KpiCard
          icon={Activity}
          tone={aiEnabled ? 'emerald' : 'rose'}
          value={aiEnabled ? 'Online' : 'Offline'}
          label="Stato Sistema AI"
          index={3}
          badge={
            <Badge
              style={{
                backgroundColor: aiEnabled ? 'var(--status-success-bg)' : 'var(--status-error-bg)',
                color: aiEnabled ? 'var(--status-success-text)' : 'var(--status-error-text)',
              }}
            >
              {aiEnabled ? 'AI Attiva' : 'AI Disattiva'}
            </Badge>
          }
          footer={
            <div className="flex items-center gap-2">
              <Switch checked={aiEnabled} onCheckedChange={setAiEnabled} />
              <span className="text-xs text-muted-foreground">{aiEnabled ? 'Disattiva' : 'Attiva'}</span>
            </div>
          }
        />
      </div>

      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
        variants={staggerContainer}
        initial="hidden"
        animate="show"
      >
        <motion.div variants={fadeSlideUp}>
          <GlassCard accentVar="var(--kpi-accent-1)">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-primary" />
                  <h4 className="text-sm font-semibold text-foreground">Andamento 7 giorni</h4>
                </div>
                <button
                  onClick={() => setActiveTab('analytics')}
                  className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors"
                >
                  Vedi dettaglio <ArrowRight className="h-3 w-3" />
                </button>
              </div>
              {trendData.length > 0 ? (
                <ResponsiveContainer width="100%" height={160}>
                  <AreaChart data={trendData} margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
                    <defs>
                      <linearGradient id="dashCostGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--chart-accent)" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="var(--chart-accent)" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="var(--chart-grid)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, className: 'fill-muted-foreground' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, className: 'fill-muted-foreground' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v: number) => `${v.toFixed(2)} €`}
                      width={50}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(v) => [`${Number(v ?? 0).toFixed(4)} €`, 'Costo']}
                      labelStyle={{ fontWeight: 600, marginBottom: 4 }}
                    />
                    <Area
                      type="monotone"
                      dataKey="costo"
                      stroke="var(--chart-accent)"
                      strokeWidth={2.5}
                      fill="url(#dashCostGrad)"
                      animationDuration={1200}
                      dot={false}
                      activeDot={{ r: 4, strokeWidth: 2, fill: 'var(--chart-accent)', stroke: 'var(--tooltip-bg)' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[160px] flex items-center justify-center text-sm text-muted-foreground">
                  Nessun dato disponibile
                </div>
              )}
            </div>
          </GlassCard>
        </motion.div>

        <motion.div variants={fadeSlideUp}>
          <GlassCard accentVar="var(--kpi-accent-2)">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-primary" />
                  <h4 className="text-sm font-semibold text-foreground">Distribuzione costi per provider</h4>
                </div>
                <button
                  onClick={() => setActiveTab('analytics')}
                  className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors"
                >
                  Vedi dettaglio <ArrowRight className="h-3 w-3" />
                </button>
              </div>
              {donutData.length > 0 ? (
                <div className="flex items-center justify-center gap-4">
                  <ResponsiveContainer width={130} height={130}>
                    <PieChart>
                      <Pie
                        data={donutData}
                        cx="50%"
                        cy="50%"
                        innerRadius={35}
                        outerRadius={60}
                        dataKey="value"
                        paddingAngle={3}
                        animationDuration={1000}
                        strokeWidth={0}
                      >
                        {donutData.map((entry, idx) => (
                          <Cell key={idx} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={(v) => [`${Number(v ?? 0).toFixed(4)} €`, 'Costo']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-col gap-1.5">
                    {donutData.map((d, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                        <span className="text-muted-foreground">{d.name}</span>
                        <span className="font-medium text-foreground">{d.value.toFixed(2)} €</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-[130px] flex items-center justify-center text-sm text-muted-foreground">
                  Nessun dato disponibile
                </div>
              )}
            </div>
          </GlassCard>
        </motion.div>
      </motion.div>

      <motion.div variants={fadeSlideUp} initial="hidden" animate="show">
        <GlassCard accentVar="var(--kpi-accent-1)">
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-primary" />
                <h4 className="text-sm font-semibold text-foreground">{`Agenti — Attività recente`}</h4>
              </div>
              <button
                onClick={() => setActiveTab('agents')}
                className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors"
              >
                Vedi tutti <ArrowRight className="h-3 w-3" />
              </button>
            </div>
            {topAgents.length > 0 ? (
              <div className="space-y-2">
                {topAgents.map((agent, i) => (
                  <motion.div
                    key={agent.id}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15 + i * 0.06, duration: 0.35 }}
                    className="flex items-center gap-3 px-3 py-2 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <agent.icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-sm text-foreground flex-1 truncate font-medium">{agent.name}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${agent.typeColor}`}>
                      {agent.type}
                    </span>
                    <span
                      className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                      style={{
                        backgroundColor: agent.is_active ? 'var(--status-success-bg)' : undefined,
                        color: agent.is_active ? 'var(--status-success-text)' : undefined,
                      }}
                    >
                      {agent.is_active ? 'Attivo' : 'Inattivo'}
                    </span>
                    <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden flex-shrink-0">
                      <motion.div
                        className="h-full rounded-full bg-primary"
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.max((agent.usage / maxAgentUsage) * 100, agent.usage > 0 ? 8 : 0)}%` }}
                        transition={{ duration: 0.8, delay: 0.3 + i * 0.08, ease: 'easeOut' }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="py-6 text-center text-sm text-muted-foreground">Nessun agente configurato</div>
            )}
          </div>
        </GlassCard>
      </motion.div>

      <motion.div variants={fadeSlideUp} initial="hidden" animate="show" transition={{ delay: 0.25 }}>
        <GlassCard accentVar="var(--health-accent)">
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" />
                <h4 className="text-sm font-semibold text-foreground">Salute sistema</h4>
              </div>
              <button
                onClick={() => setActiveTab('rag-analytics')}
                className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors"
              >
                Dettaglio RAG <ArrowRight className="h-3 w-3" />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {([
                { icon: Gauge, label: 'Latenza RAG media', value: ragLatency },
                { icon: Activity, label: 'Success rate vocali', value: elSuccessRate },
                { icon: Database, label: 'Cache hit rate RAG', value: ragCacheRate },
                { icon: Zap, label: 'Uptime provider', value: providerUptime },
              ] as const).map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.92 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 + i * 0.07, duration: 0.35 }}
                  className="rounded-xl bg-muted p-3.5 text-center transition-all hover:scale-[1.02]"
                >
                  <item.icon className="h-5 w-5 text-primary mx-auto mb-1.5" />
                  <p className="text-lg font-bold text-foreground">{item.value}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{item.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </GlassCard>
      </motion.div>
    </div>
  );
}
